export const anatomy2Module = {
  id: "anatomy2",
  title: "Kidney Anatomy: Part 2",
  sections: [
    {
      type: "text",
      content: `<h3>Gerota's Fascia (Renal Fascia)</h3>
      <p>Each kidney is enclosed within a layered system of connective tissue and fat. The innermost layer is the <b class="purple-term">renal capsule</b> — a tough fibrous membrane that directly wraps the kidney. Surrounding this is the <b class="purple-term">perirenal fat</b> (perinephric fat), a cushion of adipose tissue that helps protect the kidney from trauma.</p>
      <p>Enclosing both the kidney and its perirenal fat is the <b class="purple-term">renal fascia</b>, also known as <b class="purple-term">Gerota's fascia</b>. This is a dense fibrous sheath with two layers:
      <ul>
        <li>An <b>anterior layer</b> (also called Toldt's fascia)</li>
        <li>A <b>posterior layer</b></li>
      </ul>
      The two layers fuse superiorly around the adrenal gland, enclosing it within the same fascial compartment as the kidney. Laterally they also fuse, but inferiorly they remain open — this is clinically important as it means urine or blood can track downward out of the fascial compartment.</p>
      <p>Outside Gerota's fascia lies the <b class="purple-term">pararenal fat</b> (paranephric fat), a further layer of fat between the fascia and the surrounding abdominal wall muscles.</p>
      <div class="highlight-box">
        <b>Layers from inside out:</b><br/>
        Renal capsule → Perirenal fat → Gerota's fascia → Pararenal fat → Abdominal wall muscles
      </div>
      <p><b>Clinical relevance:</b> Gerota's fascia acts as a barrier that can contain perirenal haematomas and abscesses. In renal cell carcinoma (RCC), tumour confined within Gerota's fascia is staged as T3a — spread beyond it indicates a more advanced stage. Surgeons use Gerota's fascia as a dissection plane during radical nephrectomy.</p>
      `,
      image: "/images/kidney/kidneylayers.png",
    },
    {
      type: "question",
      text: "What is the name of the fibrous sheath that encloses the kidney and its surrounding fat?",
      options: [
        "Renal capsule",
        "Gerota's fascia",
        "Toldt's fascia",
        "Pararenal fascia",
      ],
      correct: 1,
      explanation:
        "Gerota's fascia (renal fascia) is the fibrous sheath that encloses the kidney, its perirenal fat, and the adrenal gland. The renal capsule is the innermost layer that directly wraps the kidney itself.",
    },
    {
      type: "question",
      text: "Between the renal capsule and Gerota's fascia, what is found?",
      options: [
        "Pararenal fat",
        "Perirenal fat",
        "Retroperitoneal fat",
        "Visceral peritoneum",
      ],
      correct: 1,
      explanation:
        "Perirenal fat (perinephric fat) sits between the renal capsule and Gerota's fascia. Pararenal fat lies outside Gerota's fascia.",
    },
    {
      type: "question",
      text: "Gerota's fascia fuses superiorly around the adrenal gland but remains open inferiorly. What is the clinical significance of this?",
      options: [
        "It prevents any spread of infection from the kidney",
        "Blood or urine can track downward out of the fascial compartment",
        "It ensures lymphatic drainage is directed superiorly",
        "It allows the kidney to move freely during respiration",
      ],
      correct: 1,
      explanation:
        "Because Gerota's fascia is open inferiorly, fluid collections such as blood (perinephric haematoma) or urine (urinoma) can spread downward out of the fascial compartment into the pelvis, rather than being completely contained.",
    },
    {
      type: "flashcard",
      front: "What are the layers surrounding the kidney, from inside out?",
      back: `<ul>
        <li><b>Renal capsule</b> — directly wraps the kidney</li>
        <li><b>Perirenal fat</b> (perinephric fat) — cushion between capsule and fascia</li>
        <li><b>Gerota's fascia</b> (renal fascia) — encloses kidney, perirenal fat, and adrenal gland</li>
        <li><b>Pararenal fat</b> (paranephric fat) — outside the fascia</li>
      </ul>`,
      hint: "Think from the kidney surface outward",
    },
    {
      type: "text",
      content: `<h3>Relations of the Kidney</h3>
      <p>Each kidney sits in the <b class="purple-term">retroperitoneum</b>, meaning it lies behind the peritoneum on the posterior abdominal wall. It is important to know what structures are adjacent to the kidneys, as this determines which structures may be injured in renal trauma, and which organs are seen adjacent to the kidney on imaging.</p>

      <h3>Posterior relations (same for both kidneys)</h3>
      <p>The posterior surface of both kidneys is related to the same structures:
      <ul>
        <li><b class="purple-term">Diaphragm</b> — the superior pole of the kidney lies against the diaphragm</li>
        <li><b class="purple-term">Quadratus lumborum</b> — lies medially and posteriorly</li>
        <li><b class="purple-term">Psoas major</b> — lies medially; the kidney rests on this muscle</li>
        <li><b class="purple-term">Transversus abdominis</b> — lies laterally</li>
        <li><b class="purple-term">12th rib</b> — crosses the posterior surface of the kidney</li>
        <li><b>Subcostal nerve (T12), iliohypogastric nerve, and ilioinguinal nerve</b> — pass posterior to the kidney</li>
      </ul>
      </p>
      <div class="highlight-box">
        <b>Posterior relations (both kidneys):</b> Diaphragm · Quadratus lumborum · Psoas major · Transversus abdominis · 12th rib · Subcostal, iliohypogastric and ilioinguinal nerves
      </div>

      <h3>Anterior relations of the right kidney</h3>
      <p>
      <ul>
        <li><b class="purple-term">Liver</b> — the right lobe of the liver overlies the superior portion of the right kidney. The space between them is called <b class="purple-term">Morrison's pouch</b> (hepatorenal pouch), which is the most dependent part of the peritoneal cavity when supine — free fluid collects here first.</li>
        <li><b class="purple-term">Duodenum</b> — the second part (descending) of the duodenum lies against the medial aspect of the right kidney</li>
        <li><b class="purple-term">Right colic flexure</b> (hepatic flexure) — lies anteriorly at the lower pole</li>
        <li><b>Small intestine</b> — covers the lower portion</li>
      </ul>
      </p>

      <h3>Anterior relations of the left kidney</h3>
      <p>
      <ul>
        <li><b class="purple-term">Spleen</b> — overlies the upper lateral portion</li>
        <li><b class="purple-term">Stomach</b> — lies anteriorly near the upper pole</li>
        <li><b class="purple-term">Pancreas</b> (tail) — crosses the anterior surface of the left kidney</li>
        <li><b class="purple-term">Left colic flexure</b> (splenic flexure) — lies anteriorly at the lower pole</li>
        <li><b>Descending colon and small intestine</b> — cover the lower portion</li>
      </ul>
      </p>
      `,
      image: "/images/kidney/kidneyrelations.png",
    },
    {
      type: "question",
      text: "Morrison's pouch (the hepatorenal pouch) is formed between which two structures?",
      options: [
        "Right kidney and the duodenum",
        "Right kidney and the liver",
        "Left kidney and the spleen",
        "Left kidney and the pancreas",
      ],
      correct: 1,
      explanation:
        "Morrison's pouch is the potential space between the right lobe of the liver and the right kidney. It is the most dependent part of the peritoneal cavity when a patient is supine, so free fluid (e.g. blood from trauma) collects here first — it is a key target in FAST ultrasound.",
    },
    {
      type: "question",
      text: "Which part of the duodenum is directly related to the right kidney?",
      options: [
        "First part (superior)",
        "Second part (descending)",
        "Third part (horizontal)",
        "Fourth part (ascending)",
      ],
      correct: 1,
      explanation:
        "The second part (descending) of the duodenum lies against the medial aspect of the right kidney. This is why duodenal injuries can sometimes present with retroperitoneal signs.",
    },
    {
      type: "question",
      text: "Which of the following is an anterior relation of the LEFT kidney?",
      options: [
        "Liver",
        "Duodenum (2nd part)",
        "Tail of the pancreas",
        "Right colic flexure",
      ],
      correct: 2,
      explanation:
        "The tail of the pancreas crosses the anterior surface of the left kidney. The liver and 2nd part of the duodenum are anterior relations of the right kidney. The right colic (hepatic) flexure is also a relation of the right kidney.",
    },
    {
      type: "question",
      text: "Which nerve passes posterior to both kidneys and can be injured during renal surgery?",
      options: [
        "Femoral nerve",
        "Genitofemoral nerve",
        "Subcostal nerve (T12)",
        "Obturator nerve",
      ],
      correct: 2,
      explanation:
        "The subcostal nerve (T12), together with the iliohypogastric and ilioinguinal nerves, passes posterior to the kidneys. These nerves can be injured during flank incisions or renal surgery, causing numbness or pain in the groin and scrotum/labia.",
    },
    {
      type: "flashcard",
      front: "What are the anterior relations of the right kidney?",
      back: `<ul>
        <li><b>Liver</b> (right lobe) — superior</li>
        <li><b>Duodenum</b> (2nd part) — medial</li>
        <li><b>Right colic flexure</b> (hepatic flexure) — lower pole</li>
        <li><b>Small intestine</b> — lower portion</li>
      </ul>`,
      hint: "Think right side of the abdomen — liver, duodenum, hepatic flexure",
    },
    {
      type: "flashcard",
      front: "What are the anterior relations of the left kidney?",
      back: `<ul>
        <li><b>Spleen</b> — upper lateral</li>
        <li><b>Stomach</b> — upper pole</li>
        <li><b>Tail of pancreas</b> — crosses anterior surface</li>
        <li><b>Left colic flexure</b> (splenic flexure) — lower pole</li>
        <li><b>Descending colon and small intestine</b> — lower portion</li>
      </ul>`,
      hint: "Think left side — spleen, stomach, pancreatic tail, splenic flexure",
    },
    {
      type: "text",
      content: `<h3>The Ureter</h3>
      <p>The <b class="purple-term">ureter</b> is a muscular tube approximately <b>25–30 cm long</b> that carries urine from the renal pelvis down to the urinary bladder. It has thick smooth muscle walls that contract in peristaltic waves to propel urine downward.</p>

      <p>The ureter runs in the <b>retroperitoneum</b> along the anterior surface of the psoas major muscle, then crosses over the <b class="purple-term">bifurcation of the common iliac artery</b> at the pelvic brim before descending into the pelvis to enter the bladder.</p>

      <h3>Three natural narrowings of the ureter</h3>
      <p>There are three points along the ureter where it is naturally narrowed. These are the sites where <b class="purple-term">kidney stones</b> are most likely to become <b>impacted</b>:
      <ol>
        <li><b class="purple-term">Pelviureteric junction (PUJ)</b> — where the renal pelvis transitions into the ureter. This is the narrowest point and the most common site for obstruction.</li>
        <li><b class="purple-term">Pelvic brim</b> — where the ureter crosses over the iliac vessels (bifurcation of the common iliac artery). This is sometimes called the "crossing of the iliac vessels".</li>
        <li><b class="purple-term">Vesicoureteric junction (VUJ)</b> — where the ureter enters the bladder wall. The ureter runs obliquely through the bladder wall here, which creates a valve-like mechanism that prevents reflux of urine back up into the ureter.</li>
      </ol>
      </p>
      <div class="highlight-box">
        <b>Three sites of ureteric narrowing (commonest stone impaction sites):</b><br/>
        1. Pelviureteric junction (PUJ)<br/>
        2. Crossing of the iliac vessels (pelvic brim)<br/>
        3. Vesicoureteric junction (VUJ)
      </div>

      <h3>Blood supply of the ureter</h3>
      <p>The ureter has a segmental blood supply from multiple vessels at different levels:
      <ul>
        <li><b>Upper ureter</b>: branches from the <b class="purple-term">renal artery</b></li>
        <li><b>Middle ureter</b>: branches from the <b class="purple-term">gonadal artery</b> (testicular or ovarian) and the aorta</li>
        <li><b>Lower ureter / pelvic ureter</b>: branches from the <b class="purple-term">superior and inferior vesical arteries</b></li>
      </ul>
      </p>
      <p>This segmental supply means that the ureter must be handled carefully during surgery — stripping it of its adventitia (the outer connective tissue sheath through which the vessels run) can devascularise it and lead to necrosis or stricture formation.</p>

      <h3>Important clinical relation: ureter and the uterine artery</h3>
      <p>In females, as the ureter enters the pelvis it passes <b>under the uterine artery</b> — the ureter runs beneath the artery at the level of the cervix. This relationship is remembered by the mnemonic <b>"water under the bridge"</b> (ureter = water, uterine artery = bridge).</p>
      <p>This is critically important during hysterectomy — the uterine artery must be ligated carefully to avoid accidentally cutting the ureter, which lies just below it.</p>
      `,
      image: "/images/kidney/ureter.png",
    },
    {
      type: "question",
      text: "A 35-year-old man presents with severe colicky right flank pain radiating to the groin. A kidney stone is found on CT. At which site is a ureteric stone most likely to become impacted?",
      options: [
        "Mid-ureter, along the psoas muscle",
        "Pelviureteric junction (PUJ)",
        "Where the ureter crosses the renal artery",
        "Within the renal pelvis",
      ],
      correct: 1,
      explanation:
        "The pelviureteric junction (PUJ) is the narrowest point of the ureter and the most common site for stone impaction. The other narrowings are at the pelvic brim (iliac vessel crossing) and the vesicoureteric junction (VUJ).",
    },
    {
      type: "question",
      text: "At which anatomical landmark does the ureter cross as it passes from the abdomen into the pelvis?",
      options: [
        "Bifurcation of the abdominal aorta",
        "Bifurcation of the common iliac artery",
        "Inguinal ligament",
        "Sacral promontory",
      ],
      correct: 1,
      explanation:
        "The ureter crosses over the bifurcation of the common iliac artery at the pelvic brim. This crossing point is one of the three natural narrowings where kidney stones can become impacted.",
    },
    {
      type: "question",
      text: "During a hysterectomy, a surgeon must be careful not to injure the ureter. What is the anatomical relationship between the ureter and the uterine artery?",
      options: [
        "The uterine artery passes beneath the ureter",
        "The ureter passes beneath the uterine artery",
        "The ureter and uterine artery run side by side",
        "The uterine artery passes through the ureter",
      ],
      correct: 1,
      explanation:
        "The ureter passes beneath the uterine artery at the level of the cervix. This is remembered by the mnemonic 'water under the bridge' — the ureter (water) passes under the uterine artery (bridge). Failure to recognise this can result in accidental ureteric ligation or transection during hysterectomy.",
    },
    {
      type: "question",
      text: "What is the approximate length of the ureter?",
      options: ["5–10 cm", "10–15 cm", "25–30 cm", "40–45 cm"],
      correct: 2,
      explanation:
        "The ureter is approximately 25–30 cm long, running from the renal pelvis to the urinary bladder. It travels retroperitoneally along the anterior surface of the psoas muscle.",
    },
    {
      type: "flashcard",
      front:
        "What are the three natural narrowings of the ureter where kidney stones commonly become impacted?",
      back: `<ol>
        <li><b>Pelviureteric junction (PUJ)</b> — where the renal pelvis meets the ureter (narrowest point)</li>
        <li><b>Pelvic brim</b> — where the ureter crosses the bifurcation of the common iliac artery</li>
        <li><b>Vesicoureteric junction (VUJ)</b> — where the ureter enters the bladder wall</li>
      </ol>`,
      hint: "Top, middle, and bottom of the ureter",
    },
    {
      type: "flashcard",
      front: "What is the blood supply to the ureter at each level?",
      back: `<ul>
        <li><b>Upper ureter:</b> Renal artery</li>
        <li><b>Middle ureter:</b> Gonadal artery (testicular/ovarian) and aorta</li>
        <li><b>Lower/pelvic ureter:</b> Superior and inferior vesical arteries</li>
      </ul>
      <p>The supply is segmental — stripping the adventitia risks devascularisation.</p>`,
      hint: "Three levels, three different sources",
    },
  ],
};
